import { useCallback, useMemo } from 'react';

import { useWorkspacesPageSearchUsersLazyQuery } from '../../../generated';

export interface UserOption {
  id: string;
  name: string | null;
  email: string | null;
  phone: string | null;
}

export interface UseUserSearchResult {
  users: UserOption[];
  isSearching: boolean;
  searchUsers: (searchTerm: string) => void;
}

/** User search only answers usefully from the third character. */
export const USER_SEARCH_MIN_CHARS = 3;

const RESULT_LIMIT = 30;

/**
 * Choco users to add to a workspace.
 *
 * Unlike the supplier picker there is no first page: the user directory is far
 * too large to show a meaningless slice of, so nothing is fetched until there
 * is something to search for.
 */
export const useUserSearch = (): UseUserSearchResult => {
  const [executeSearch, { data, loading }] =
    useWorkspacesPageSearchUsersLazyQuery({ fetchPolicy: 'cache-and-network' });

  const searchUsers = useCallback(
    (searchTerm: string) => {
      const keywords = searchTerm.trim();

      if (keywords.length < USER_SEARCH_MIN_CHARS) {
        return;
      }

      executeSearch({
        variables: { query: { keywords }, limit: RESULT_LIMIT },
      });
    },
    [executeSearch]
  );

  const users = useMemo(() => {
    const search = data?.userSearch;

    // Narrowed on `edges` rather than `__typename`: codegen types the latter as
    // optional on every member of the union, so it discriminates neither arm.
    if (!search || !('edges' in search)) {
      return [];
    }

    return search.edges.map((edge) => ({
      id: edge.node.id,
      name: edge.node.name ?? null,
      email: edge.node.email ?? null,
      phone: edge.node.phone ?? null,
    }));
  }, [data]);

  return { users, isSearching: loading, searchUsers };
};
